import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {useAppDispatch, useAppSelector} from '../store/hooks';
import {
  adminUsersApiSelector,
  adminUsersPageSelector,
  adminUsersSelector,
  adminUsersTotalSelector,
} from '../store/selectors/admin.selectors';
import {fetchAdminUsers} from '../store/slices/adminThunks';
import {
  ADMIN_USERS_PAGE_SIZE,
  AdminSortOrder,
  AdminSubscriptionPlan,
  AdminUserSortField,
  AdminUserStatusFilter,
} from '../domain/admin';
import AdminUserFilters, {AdminUserFilterValues} from './admin/AdminUserFilters';
import AdminUserTable from './admin/AdminUserTable';
import {Pagination, Panel} from './admin/AdminPrimitives';

/**
 * The user table's logic: filters, sort, paging and the fetch.
 *
 * **The query lives in the URL**, not in component state. An admin screen is one people paste at
 * each other — "the failed-signup user is in here" — and a filtered list that survives a reload and
 * can be linked to is worth the small amount of parsing. It also means the dashboard's tiles can
 * deep-link into a pre-filtered table rather than dropping you at an unfiltered one.
 *
 * The search box is the exception: it is typed into, so it holds local state and is debounced into
 * the URL. Writing every keystroke to the URL would fire a request per character and fill the
 * history stack.
 */

const SORT_FIELDS: AdminUserSortField[] = [
  'created_at',
  'email',
  'name',
  'subscription_expires_at',
  'transcription_seconds_total',
  'transcription_count',
  'conversion_count',
  'last_transcription_at',
  'last_conversion_at',
  'transcriptions_in_flight',
  'transcriptions_failed',
];

const PLANS: AdminSubscriptionPlan[] = ['free', 'pro', 'team'];

const SEARCH_DEBOUNCE_MS = 300;

const AdminUsersContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  const users = useAppSelector(adminUsersSelector);
  const total = useAppSelector(adminUsersTotalSelector);
  const page = useAppSelector(adminUsersPageSelector);
  const api = useAppSelector(adminUsersApiSelector);

  // Everything the endpoint takes, read back out of the URL and validated — an unrecognised value
  // in a hand-edited query string falls back rather than being sent on to the API.
  const search = searchParams.get('search') ?? '';
  const statusParam = searchParams.get('status') as AdminUserStatusFilter | null;
  const status: AdminUserStatusFilter | ''
    = statusParam === 'active' || statusParam === 'inactive' ? statusParam : '';
  const planParam = searchParams.get('plan') as AdminSubscriptionPlan | null;
  const plan = planParam && PLANS.includes(planParam) ? planParam : '';
  const passwordSetFalse = searchParams.get('password_set') === 'false';
  const inFlight = searchParams.get('in_flight') === 'true';
  const createdAfter = searchParams.get('created_after') ?? '';
  const sortParam = searchParams.get('sort') as AdminUserSortField | null;
  const sort: AdminUserSortField
    = sortParam && SORT_FIELDS.includes(sortParam) ? sortParam : 'created_at';
  const order: AdminSortOrder = searchParams.get('order') === 'asc' ? 'asc' : 'desc';
  const offset = Math.max(0, Number(searchParams.get('offset') ?? 0) || 0);

  const [searchInput, setSearchInput] = useState(search);

  // Keep the box in step when the URL changes from somewhere else (a tile link, the back button).
  useEffect(() => {
    setSearchInput(search);
  }, [search]);

  const updateParams = useCallback(
    (patch: Record<string, string | undefined>, options?: {keepOffset?: boolean}) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);

          Object.entries(patch).forEach(([key, value]) => {
            if (value === undefined || value === '') {
              next.delete(key);
            } else {
              next.set(key, value);
            }
          });

          // Any change to what is being listed puts you back on page one: staying on offset 200 of
          // a result set that now has 12 rows shows an empty table and looks like a broken filter.
          if (!options?.keepOffset) {
            next.delete('offset');
          }

          return next;
        },
        {replace: true}
      );
    },
    [setSearchParams]
  );

  useEffect(() => {
    if (searchInput === search) {
      return;
    }

    const timer = window.setTimeout(() => {
      updateParams({search: searchInput || undefined});
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [searchInput, search, updateParams]);

  const query = useMemo(
    () => ({
      search: search || undefined,
      status: status || undefined,
      plan: plan || undefined,
      passwordSetFalse: passwordSetFalse || undefined,
      inFlight: inFlight || undefined,
      createdAfter: createdAfter || undefined,
      sort,
      order,
      limit: ADMIN_USERS_PAGE_SIZE,
      offset,
    }),
    [search, status, plan, passwordSetFalse, inFlight, createdAfter, sort, order, offset]
  );

  useEffect(() => {
    dispatch(fetchAdminUsers(query));
  }, [dispatch, query]);

  const values: AdminUserFilterValues = useMemo(
    () => ({search, status, plan, passwordSetFalse, inFlight, createdAfter}),
    [search, status, plan, passwordSetFalse, inFlight, createdAfter]
  );

  const handleSortChange = (field: AdminUserSortField) => {
    // Clicking the active column flips direction; a new column starts descending, which is the
    // useful end of every one of these (newest, biggest, most recent).
    const nextOrder: AdminSortOrder = sort === field && order === 'desc' ? 'asc' : 'desc';
    updateParams({sort: field, order: nextOrder});
  };

  const hasFilters = Boolean(
    search || status || plan || passwordSetFalse || inFlight || createdAfter
  );

  return (
    <Panel
      title="Users"
      subtitle="Plan badges read the derived subscription state, not the Paddle mirror."
      padded={false}
    >
      <div className="border-b border-gray-800 px-4 py-3">
        <AdminUserFilters
          values={values}
          searchInput={searchInput}
          onSearchInput={setSearchInput}
          onChange={(patch) => {
            // Only the keys the control actually changed are written; `updateParams` deletes any
            // it is handed as undefined, so a patch of one field must not mention the others.
            const next: Record<string, string | undefined> = {};
            if ('status' in patch) {
              next.status = patch.status || undefined;
            }
            if ('plan' in patch) {
              next.plan = patch.plan || undefined;
            }
            if ('passwordSetFalse' in patch) {
              next.password_set = patch.passwordSetFalse ? 'false' : undefined;
            }
            if ('inFlight' in patch) {
              next.in_flight = patch.inFlight ? 'true' : undefined;
            }
            if ('createdAfter' in patch) {
              next.created_after = patch.createdAfter || undefined;
            }
            updateParams(next);
          }}
          onClear={() => updateParams({
            search: undefined,
            status: undefined,
            plan: undefined,
            password_set: undefined,
            in_flight: undefined,
            created_after: undefined,
          })}
          resultCount={total}
          isLoading={api.status === 'pending'}
        />
      </div>

      <AdminUserTable
        users={users}
        isLoading={api.status === 'pending'}
        error={api.status === 'rejected' ? api.error : null}
        onRetry={() => dispatch(fetchAdminUsers(query))}
        sort={sort}
        order={order}
        onSortChange={handleSortChange}
        hasFilters={hasFilters}
      />

      <Pagination
        total={total}
        limit={page?.limit || ADMIN_USERS_PAGE_SIZE}
        offset={offset}
        onOffsetChange={next => updateParams({offset: String(next)}, {keepOffset: true})}
        noun="users"
        isLoading={api.status === 'pending'}
      />
    </Panel>
  );
};

export default AdminUsersContainer;
